/**
 * Helper to cache job data into IndexedDB for offline access on native.
 * Called by dashboard sections after successful server fetches.
 */

import { isNative } from "@/lib/capacitor";
import { jobsCache, type CachedJob } from "@/lib/offlineDb";

/**
 * Cache an array of job objects to IndexedDB (native only).
 * Merges with existing cache — doesn't delete jobs not in this batch
 * (since different sections fetch different subsets).
 */
export async function cacheJobsForOffline(jobs: any[]): Promise<void> {
  if (!isNative) return;
  if (!jobs || jobs.length === 0) return;

  try {
    for (const job of jobs) {
      if (!job.id) continue;
      const cached: CachedJob = {
        job_id: job.id,
        company_id: job.company_id || "",
        assigned_user_id: job.driver_id || "",
        payload_json: JSON.stringify(job),
        updated_at: Date.now(),
      };
      await jobsCache.put(cached);
    }
    console.log(`[JobCache] Cached ${jobs.length} jobs to IndexedDB`);
  } catch (err) {
    console.warn("[JobCache] Failed to cache jobs:", err);
  }
}

/**
 * Try to load a single job from IndexedDB cache.
 * Returns the parsed job object or null.
 */
export async function getJobFromCache(jobId: string, companyId?: string | null): Promise<any | null> {
  try {
    const cached = await jobsCache.get(jobId);
    if (cached && (!companyId || cached.company_id === companyId)) {
      return JSON.parse(cached.payload_json);
    }
    return null;
  } catch (err) {
    console.warn("[JobCache] Failed to read cache:", err);
    return null;
  }
}

/**
 * Get all cached jobs from IndexedDB, optionally filtered.
 * Used as offline fallback for dashboard sections.
 */
export async function getAllCachedJobs(companyId?: string | null): Promise<any[]> {
  try {
    const cached = await jobsCache.getAll();
    return cached
      .filter(c => !companyId || c.company_id === companyId)
      .map(c => JSON.parse(c.payload_json));
  } catch (err) {
    console.warn("[JobCache] Failed to read all cached jobs:", err);
    return [];
  }
}

/**
 * Get cached pending jobs (collection not completed, status pending/in_progress).
 */
export async function getCachedPendingJobs(companyId?: string | null): Promise<any[]> {
  const all = await getAllCachedJobs(companyId);
  return all.filter(j =>
    (j.status === "pending" || j.status === "in_progress") &&
    !j.collection_completed_at
  );
}

/**
 * Get cached awaiting delivery jobs (collection completed or skipped, not delivered).
 */
export async function getCachedAwaitingDeliveryJobs(companyId?: string | null): Promise<any[]> {
  const all = await getAllCachedJobs(companyId);
  return all.filter(j =>
    j.status === "awaiting_delivery" &&
    !j.delivery_completed_at &&
    !j.completed_at
  );
}
